// Turns arbitrary values (console arguments, rejection reasons, metadata) into
// JSON-safe, size-limited structures. Never throws.

const DEFAULTS = { depth: 3, maxLength: 1000, maxKeys: 50 };

export function truncate(value, max) {
  const str = typeof value === 'string' ? value : String(value);
  if (str.length <= max) return str;
  return `${str.slice(0, max)}… [${str.length - max} more characters]`;
}

export function isError(value) {
  return (
    value instanceof Error ||
    (value !== null &&
      typeof value === 'object' &&
      typeof value.message === 'string' &&
      typeof value.name === 'string' &&
      'stack' in value)
  );
}

function isNode(value) {
  return typeof Node !== 'undefined' && value instanceof Node;
}

function isWindow(value) {
  return value !== null && typeof value === 'object' && value.window === value;
}

function constructorName(value) {
  try {
    const name = Object.getPrototypeOf(value)?.constructor?.name;
    return name || 'Object';
  } catch {
    return 'Object';
  }
}

/** `<button#book.btn.primary>` style description of a DOM node. */
export function describeNode(node) {
  if (!node) return '';
  if (node.nodeType === 1) {
    const el = /** @type {Element} */ (node);
    // getAttribute, not el.id / el.className, which form fields can "clobber".
    let out = el.tagName.toLowerCase();
    const id = el.getAttribute('id');
    if (id) out += `#${id}`;
    const classes = (el.getAttribute('class') || '').trim().split(/\s+/).filter(Boolean);
    if (classes.length) out += `.${classes.slice(0, 3).join('.')}`;
    return `<${out}>`;
  }
  if (node.nodeType === 3) return `#text "${truncate(node.textContent.trim(), 40)}"`;
  if (node.nodeType === 9) return '#document';
  return `#${node.nodeName}`;
}

/**
 * @param {unknown} value
 * @param {Partial<typeof DEFAULTS>} [options]
 * @returns {unknown} JSON-safe value
 */
export function serialise(value, options = {}) {
  const opts = { ...DEFAULTS, ...options };
  const seen = new WeakSet();

  const walk = (v, depth) => {
    if (v === null) return null;
    switch (typeof v) {
      case 'string':
        return truncate(v, opts.maxLength);
      case 'number':
        return Number.isFinite(v) ? v : String(v);
      case 'boolean':
        return v;
      case 'undefined':
        return '[undefined]';
      case 'bigint':
        return `${v}n`;
      case 'symbol':
        return v.toString();
      case 'function':
        return `[Function ${v.name || 'anonymous'}]`;
      default:
        break;
    }
    try {
      if (isError(v)) {
        const out = {
          name: v.name,
          message: truncate(v.message, opts.maxLength),
        };
        if (v.stack) out.stack = truncate(String(v.stack), opts.maxLength * 4);
        if (v.cause !== undefined && depth < opts.depth) out.cause = walk(v.cause, depth + 1);
        return out;
      }
      if (isNode(v)) return describeNode(v);
      if (isWindow(v)) return '[Window]';
      if (seen.has(v)) return '[Circular]';
      if (v instanceof Date) return Number.isNaN(v.getTime()) ? 'Invalid Date' : v.toISOString();
      if (v instanceof RegExp) return v.toString();
      if (typeof Promise !== 'undefined' && v instanceof Promise) return '[Promise]';
      if (v instanceof ArrayBuffer) return `[ArrayBuffer ${v.byteLength} bytes]`;
      if (ArrayBuffer.isView(v)) return `[${constructorName(v)} ${v.byteLength} bytes]`;
      if (typeof Blob !== 'undefined' && v instanceof Blob) {
        return `[${constructorName(v)} ${v.type || 'unknown type'} ${v.size} bytes]`;
      }

      if (depth >= opts.depth) {
        if (Array.isArray(v)) return `[Array(${v.length})]`;
        if (v instanceof Map) return `[Map(${v.size})]`;
        if (v instanceof Set) return `[Set(${v.size})]`;
        return `[${constructorName(v)}]`;
      }
      // `seen` holds only the current chain of ancestors, so an object that appears
      // twice side by side is serialised twice, and only true cycles become [Circular].
      seen.add(v);
      try {
        return walkContainer(v, depth);
      } finally {
        seen.delete(v);
      }
    } catch (error) {
      return `[Unserialisable: ${error && error.message}]`;
    }
  };

  const walkContainer = (v, depth) => {
    {
      if (Array.isArray(v)) {
        const out = v.slice(0, opts.maxKeys).map((item) => walk(item, depth + 1));
        if (v.length > opts.maxKeys) out.push(`… ${v.length - opts.maxKeys} more items`);
        return out;
      }
      if (v instanceof Map) {
        const entries = [];
        for (const [key, item] of v) {
          if (entries.length >= opts.maxKeys) break;
          entries.push([walk(key, depth + 1), walk(item, depth + 1)]);
        }
        return { '[Map]': entries };
      }
      if (v instanceof Set) {
        const items = [];
        for (const item of v) {
          if (items.length >= opts.maxKeys) break;
          items.push(walk(item, depth + 1));
        }
        return { '[Set]': items };
      }

      const out = {};
      const name = constructorName(v);
      if (name !== 'Object' && name !== '') out['[type]'] = name;
      const keys = Object.keys(v);
      for (const key of keys.slice(0, opts.maxKeys)) {
        try {
          // Read the descriptor rather than v[key], so getters (and the reactive
          // tracking in frameworks such as Vue or MobX) never run because we logged.
          const descriptor = Object.getOwnPropertyDescriptor(v, key);
          out[key] = descriptor && 'value' in descriptor ? walk(descriptor.value, depth + 1) : '[Getter]';
        } catch (error) {
          out[key] = `[Unreadable: ${error && error.message}]`;
        }
      }
      if (keys.length > opts.maxKeys) out['…'] = `${keys.length - opts.maxKeys} more keys`;
      return out;
    }
  };

  return walk(value, 0);
}

export function safeStringify(value) {
  try {
    const json = JSON.stringify(value);
    return json === undefined ? String(value) : json;
  } catch {
    return String(value);
  }
}

/** One-line text form of a value, for titles. */
export function inlineText(value, max = 200) {
  if (typeof value === 'string') return truncate(value, max);
  if (isError(value)) return truncate(`${value.name}: ${value.message}`, max);
  const s = serialise(value, { depth: 1, maxLength: max, maxKeys: 10 });
  return truncate(typeof s === 'string' ? s : safeStringify(s), max);
}

/**
 * Formats console arguments the way DevTools roughly does: applies %s/%d/%i/%f/%o/%O
 * substitutions, drops %c styling, and returns a short title plus serialised arguments.
 *
 * @param {unknown[]} args
 * @param {number} maxLength
 */
export function formatConsoleArgs(args, maxLength = 1000) {
  let rest = Array.from(args);
  const parts = [];
  if (typeof rest[0] === 'string' && /%[sdifoOc]/.test(rest[0])) {
    const format = rest.shift();
    const text = format.replace(/%([sdifoOc%])/g, (match, token) => {
      if (token === '%') return '%';
      if (!rest.length) return match;
      const arg = rest.shift();
      switch (token) {
        case 'c':
          return '';
        case 'd':
        case 'i':
          return String(parseInt(/** @type {any} */ (arg), 10));
        case 'f':
          return String(parseFloat(/** @type {any} */ (arg)));
        case 's':
          return typeof arg === 'string' ? arg : inlineText(arg);
        default:
          return inlineText(arg);
      }
    });
    parts.push(text);
  }
  for (const arg of rest) parts.push(inlineText(arg));
  const title = truncate(parts.join(' ').replace(/\s+/g, ' ').trim(), 200);
  return {
    title,
    args: Array.from(args, (arg) => serialise(arg, { maxLength })),
  };
}
